import { BackrsFormField } from '@backrs_ui/common-ui';

import { AutocompleteResult } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { OnboardingQuestion } from '../onboarding.types';
import { StepKey } from '../utils/stepNavigation';

type UseOnboardingFormFieldsParams = Readonly<{
  currentQuestion?: OnboardingQuestion;
  currentStep: StepKey;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: React.Dispatch<React.SetStateAction<AutocompleteResult[]>>;
  handleInputChange: (input: string, endpoint: string) => void;
}>;

export const useOnboardingFormFields = ({
  currentQuestion,
  currentStep,
  autoCompleteOptions,
  setAutoCompleteOptions,
  handleInputChange,
}: UseOnboardingFormFieldsParams): BackrsFormField[] => {
  if (!currentQuestion) {
    return [];
  }

  if (currentQuestion.question.type === 'FORM') {
    return handleFormType({
      currentQuestion,
      autoCompleteOptions,
      setAutoCompleteOptions,
      handleInputChange,
    });
  }

  return handleRadioCheckFields({
    currentQuestion,
    currentStep,
  });
};

type HandleRadioCheckFieldsParams = Readonly<{
  currentQuestion: OnboardingQuestion;
  currentStep: StepKey;
}>;

const handleRadioCheckFields = ({ currentQuestion, currentStep }: HandleRadioCheckFieldsParams) => {
  if (currentQuestion.question.type !== 'RADIO' && currentQuestion.question.type !== 'CHECKBOX') {
    return [];
  }

  const radioCheckFields: BackrsFormField[] = [
    {
      name: currentStep, // Use the step key as the field name
      label: currentQuestion.title,
      type: 'text',
      rules: currentQuestion.question.rules,
      selectOptions: {
        type: currentQuestion.question.type === 'RADIO' ? 'SINGLE' : 'MULTIPLE',
        options: currentQuestion.question.options,
      },
    },
  ];

  return radioCheckFields;
};

type HandleFormTypeParams = Readonly<{
  currentQuestion: OnboardingQuestion;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: React.Dispatch<React.SetStateAction<AutocompleteResult[]>>;
  handleInputChange: (input: string, endpoint: string) => void;
}>;

const handleFormType = ({
  currentQuestion,
  autoCompleteOptions,
  setAutoCompleteOptions,
  handleInputChange,
}: HandleFormTypeParams): BackrsFormField[] => {
  if (currentQuestion.question.type !== 'FORM') {
    return [];
  }

  return currentQuestion.question.fields.flatMap((field) => {
    // For fields that have an autocomplete endpoint, handle the transformation
    if (field.autoCompleteEndpoint) {
      const autoCompleteEndpoint = field.autoCompleteEndpoint;
      const existingOption = autoCompleteOptions.find(
        (option) => option.endpoint === autoCompleteEndpoint,
      );

      const updatedField: BackrsFormField = {
        ...field,
        selectOptions: {
          ...field.selectOptions,
          type: field.selectOptions?.type || 'MULTIPLE',
          // Use values from state if available, otherwise default to an empty array
          options:
            existingOption && existingOption.values.length > 0
              ? existingOption.values.map((value) => ({
                  label: value.label,
                  value: value.value,
                }))
              : [],
        },
        onInputChange: (input: string) => handleInputChange(input, autoCompleteEndpoint),
        onOptionSelected: () =>
          setAutoCompleteOptions((prev) =>
            prev.map((opt) =>
              opt.endpoint === autoCompleteEndpoint ? { ...opt, searchTerm: '', values: [] } : opt,
            ),
          ),
      };

      // If the endpoint is not yet in state, add it
      if (!existingOption) {
        setAutoCompleteOptions((prev) => [
          ...prev,
          { endpoint: autoCompleteEndpoint, searchTerm: '', values: [] },
        ]);
      }

      return updatedField;
    }

    if (field.type === 'tel') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\d{10}$/,
            message: 'Phone number must be 10 digits',
          },
          required: 'Phone number is required',
        },
      };

      return updatedField;
    }

    if (field.name === 'zip_code') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\d{5}$/,
            message: 'Zip code must be 5 digits',
          },
          required: 'Zip code is required',
        },
      };

      return updatedField;
    }

    if (field.type === 'email') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: 'Invalid email address',
          },
          required: 'Email is required',
        },
      };

      return updatedField;
    }

    return field;
  });
};
